import React, { useState, useEffect } from 'react';
import { FiX, FiRefreshCw, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import { showToast as toast } from '../../lib/toast';
import { trackAiTelemetry, trackEvent } from '../../lib/telemetry';
import { supabase } from '../../lib/supabaseClient';
import { getEdgeWorkerUrl } from '../../lib/edgeWorkerUrl';
import { onyxService } from '../../services/onyxService';

export default function DLQPayloadEditorModal({isOpen, onClose, event, onRefresh }) {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const [payloadText, setPayloadText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    if (isOpen && event) {
      setPayloadText(JSON.stringify(event.payload || {}, null, 2));
    }
  }, [isOpen, event]);

  if (!isOpen || !event) return null;


  const handleDryRunValidation = async () => {
    setIsValidating(true);
    setValidationResult(null);
    try {
      let parsedPayload;
      try {
        parsedPayload = JSON.parse(payloadText);
      } catch (e) {
        setValidationResult({ valid: false, error: 'Invalid JSON format. Check syntax.' });
        setIsValidating(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Session required.");

      const workerUrl = getEdgeWorkerUrl();
      const res = await fetch(`${workerUrl}/api/v1/dlq/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(parsedPayload)
      });

      const data = await res.json();
      setValidationResult(data);
    } catch (err) {
      setValidationResult({ valid: false, error: err.message });
    } finally {
      setIsValidating(false);
    }
  };

  const handleForceRetry = async () => {
    setIsSubmitting(true);
    const startTime = performance.now();
    try {
      let parsedPayload;
      try {
        parsedPayload = JSON.parse(payloadText);
      } catch (e) {
        throw new Error('Invalid JSON format. Please check syntax.');
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Session token required.");

      const workerUrl = getEdgeWorkerUrl();

      const res = await fetch(`${workerUrl}/api/dlq`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ action: 'retry', payload: parsedPayload, ticketId: event.payload?.ticket_id })
      });

      const data = await res.json();

      const latency = Math.round(performance.now() - startTime);

      if (!res.ok || !data.success) {
        trackAiTelemetry({ ticketId: event.payload?.ticket_id, actionType: 'dlq_retry', latencyMs: latency, metadata: { success: false, error: data.error }});
        throw new Error(data.error || 'Force retry failed.');
      }

      trackAiTelemetry({ ticketId: event.payload?.ticket_id, actionType: 'dlq_retry', latencyMs: latency, metadata: { success: true }});

      toast.success('Payload forced retried successfully!');
      if (onRefresh) onRefresh();
      onClose();
    } catch (err) {
      const latency = Math.round(performance.now() - startTime);
      trackAiTelemetry({ ticketId: event.payload?.ticket_id, actionType: 'dlq_retry', latencyMs: latency, metadata: { success: false, error: err.message }});
      toast.error(`Retry Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePurge = async () => {
    setIsPurging(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Session token required.");

      const workerUrl = getEdgeWorkerUrl();
      const res = await onyxService.fetchWithTimeout(
        `${workerUrl}/api/v1/admin/dlq/purge?event_id=${event.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!res.success || !res.data?.success) {
        throw new Error(res.error || res.data?.error || 'Purge failed.');
      }

      toast.success('Payload purged successfully!');
      if (onRefresh) onRefresh();
      onClose();
    } catch (err) {
      toast.error(`Purge Error: ${err.message}`);
    } finally {
      setIsPurging(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm font-mono">
      <div className="w-full max-w-3xl bg-[#0B0F19] border border-[#1F2937] rounded-2xl shadow-2xl overflow-hidden flex flex-col text-xs text-zinc-300">
        <div className="flex items-center justify-between p-4 border-b border-zinc-900 bg-zinc-900/50">
          <div className="flex items-center gap-2 text-rose-400">
            <FiAlertCircle />
            <h2 className="text-sm font-bold uppercase tracking-wider">Inspect & Edit Payload (ID: {event.id.slice(0, 8)})</h2>
          </div>
          <button onClick={onClose} className="p-2 text-zinc-300 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors">
            <FiX />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-auto min-h-[300px]">
          <p className="text-xs text-zinc-500 mb-2">Edit the JSON payload below before retrying.</p>
          <textarea
            className="w-full h-full min-h-[300px] bg-black text-emerald-400 font-mono text-xs p-4 rounded border border-[#1F2937] focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 transition-all resize-y whitespace-pre"
            value={payloadText}
            onChange={(e) => { setPayloadText(e.target.value); setValidationResult(null); }}
            spellCheck="false"
          />
        </div>

        <div className="flex flex-col p-4 border-t border-zinc-900 bg-zinc-900/50">
          {validationResult && (
            <div className={`mb-3 p-2 text-xs rounded border flex items-center gap-2 ${validationResult.valid ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-rose-500/10 border-rose-500/30 text-rose-400'}`}>
              <FiAlertCircle />
              <span>{validationResult.valid ? "Validation Passed" : validationResult.error}</span>
            </div>
          )}
          <div className="flex items-center justify-between w-full">
            <button
              onClick={handlePurge}
              disabled={isPurging || isSubmitting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase text-rose-400 bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500/20 transition-all disabled:opacity-50"
            >
              {isPurging ? <FiRefreshCw className="animate-spin" /> : <FiTrash2 />}
              <span>Discard</span>
            </button>

            <div className="flex gap-2">
              <button
                onClick={handleDryRunValidation}
                disabled={isValidating || isSubmitting || isPurging}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 hover:bg-indigo-500/20 transition-all disabled:opacity-50"
              >
                {isValidating ? <FiRefreshCw className="animate-spin" /> : <FiRefreshCw />}
                <span>Dry Run Validate</span>
              </button>

              <button
                onClick={handleForceRetry}
                disabled={isSubmitting || isPurging || (validationResult && !validationResult.valid)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all disabled:opacity-50"
              >
                {isSubmitting ? <FiRefreshCw className="animate-spin" /> : <FiRefreshCw />}
                <span>Force Retry</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
